/**
 * i18n.ts — Lightweight internationalization module for AbstractID2.
 *
 * Provides reactive Svelte stores for locale management and a translation
 * function that looks up keys in a flat key→string map per locale.
 * Untranslated keys fall back to the key itself (which is the English string).
 *
 * Exports:
 *  - `locale`  — Writable store for the current locale code (persisted to localStorage).
 *  - `locales` — Array of available locale codes derived from the translations object.
 *  - `t`       — Reactive derived store: use `$t("key")` in Svelte templates.
 *  - `t2`      — Non-reactive function for use outside component markup.
 */

import { derived, get, type Readable } from 'svelte/store';
import type { Writable } from 'svelte/store';
import { storable } from './storable';
import translations from './translations';

/** Detect the best matching locale from the browser's language preferences. */
function detectLocale(): string {
	const available = new Set(Object.keys(translations));
	for (const lang of navigator.languages ?? [navigator.language]) {
		const code = lang.split('-')[0].toLowerCase();
		if (available.has(code)) return code;
	}
	return 'en';
}

/** All available locale codes (derived from the keys of the translations object). */
export const locales: string[] = Object.keys(translations);

/** Map locale codes to flag SVG filenames (country codes). */
export const localeFlags: Record<string, string> = {
	en: 'en',
	de: 'de',
	ja: 'jp',
	ko: 'kr',
	zh: 'cn'
};

/** Current locale code, persisted to localStorage under `i18n.locale`. */
export const locale: Writable<string> = storable('i18n.locale', detectLocale(), false);

/* Validate stored locale — reset to detected (or English) if stale/invalid. */
if (!(get(locale) in localeFlags)) {
	locale.set(detectLocale());
}

/** Human-readable locale names for display. */
export const localeNames: Record<string, string> = {
	en: 'English',
	de: 'Deutsch',
	ja: '日本語',
	ko: '한국어',
	zh: '中文'
};

/**
 * Translate a key for a given locale, substituting `{{variable}}` placeholders.
 *
 * @param loc - Locale code (e.g. `"de"`).
 * @param key - The English string used as lookup key.
 * @param vars - Optional map of placeholder values.
 * @returns The translated string, or the key itself if no translation exists.
 */
function translate(loc: string, key: string, vars?: Record<string, string | number>): string {
	if (!key) return '';

	/* Look up the translation; fall back to the key (English) if missing. */
	const localeMap = (translations as Record<string, Record<string, string>>)[loc];
	let text: string = localeMap?.[key] ?? key;

	/* Defensive fallback for empty-string entries. */
	if (!text) text = key;

	/* Replace {{placeholder}} tokens with provided values. */
	if (vars != null) {
		for (const [k, v] of Object.entries(vars)) {
			text = text.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), String(v));
		}
	}

	return text;
}

/**
 * Reactive translation store — use as `$t("key")` or `$t("key", { var: "val" })`.
 * Automatically re-renders when `locale` changes.
 */
export const t: Readable<(key: string, vars?: Record<string, string | number>) => string> = derived(
	locale,
	($locale) =>
		(key: string, vars?: Record<string, string | number>) =>
			translate($locale, key, vars)
);

/**
 * Non-reactive translation — use in script blocks or callbacks where
 * store subscriptions aren't available (e.g. `placeholder={$t("Search")}`).
 */
export function t2(key: string, vars?: Record<string, string | number>): string {
	return translate(get(locale), key, vars);
}
